import {
  ErrorHandling,
  EVENT_APP_LOADING_COMPLETE,
  EVENT_APP_TERMINATING,
  FatalErrors,
  fetchWrapper,
  getUpdatedLauncherStartupOptions,
  iFrameLauncherEvent,
  iFrameLauncherMessage,
  LauncherEventType,
  Logger,
  OnAppTerminatingData,
} from "@classdojo/monsterverse-utils";
import { decode, encode } from "@msgpack/msgpack";
import ReconnectingWebSocket, { CloseEvent, Event } from "reconnecting-websocket";

import { PCAppEventHandler } from "./PCAppEventHandler";

enum WebSocketCloseCode {
  NormalClosure = 1000,
}

const failedReconnectTimeoutInSeconds = 15;
const pingTimerInSeconds = 30;
const noActivityTimeoutInSeconds = 600;

export class Networking {
  private webSocket?: ReconnectingWebSocket;
  private websocketOpenHandler?: (event: Event) => void = undefined;
  private websocketMessageHandler?: (event: MessageEvent<ArrayBuffer>) => void = undefined;
  private websocketCloseHandler?: (event: CloseEvent) => void = undefined;
  private URL?: URL;

  private receivedMessage = false;
  private reconnectTimeout?: number;
  private timeoutTimer?: number;

  private pingInterval?: number;
  private pingSentTime = Date.now();
  private receivedMessageCount = 0;
  private sentMessageCount = 0;
  private countryCode = "";
  private serverRegion = "";
  private desiredServerRegion = "";

  public loadingComplete = false;

  constructor(
    private iframeEl: HTMLIFrameElement,
    private pcAppEventHandler: PCAppEventHandler,
    private getURL: (
      islandID?: string
    ) => Promise<{ wsUrl: URL; countryCode: string; serverRegion: string; desiredServerRegion: string }>
  ) {
    window.addEventListener("message", this.onIFrameMessage.bind(this));

    pcAppEventHandler.on(EVENT_APP_LOADING_COMPLETE, () => {
      this.loadingComplete = true;
      this.resetInactivityTimer();
    });

    pcAppEventHandler.on(EVENT_APP_TERMINATING, (data: unknown) => {
      const handler = data as OnAppTerminatingData;
      if (handler) {
        const { exitingMonsterverse, customizer } = handler;
        if (!exitingMonsterverse && customizer) {
          this.loadingComplete = false;
          if (this.timeoutTimer) {
            clearTimeout(this.timeoutTimer);
            this.timeoutTimer = undefined;
          }
        }
      }
    });

    this.getURL().then(({ wsUrl, countryCode, serverRegion, desiredServerRegion }) => {
      this.countryCode = countryCode;
      this.serverRegion = serverRegion;
      this.desiredServerRegion = desiredServerRegion;
      this.connect(wsUrl);
    });
  }

  private async getWsUrl() {
    const wsUrl = this.URL!.toString();

    const env = window._ENV || "local";
    if (env !== "local") {
      const checkTicketUrl = new URL(wsUrl);
      checkTicketUrl.protocol = "https:";
      checkTicketUrl.pathname = "/checkTicket";
      try {
        const response = await fetchWrapper("ticketCheck", checkTicketUrl.toString(), { method: "GET" });
        if (!response.ok) {
          throw new Error(`ticketCheck: status failure ${await response.text()}`);
        }
      } catch (e) {
        if (e instanceof Error && "type" in e && e.type === "FetchError") {
          Logger.logInfo("ticketCheck: Failed to fetch the route");
        } else if (e instanceof Error) {
          Logger.logInfo(e.message);
        } else {
          Logger.logInfo("ticketCheck: Unknown failure");
        }
        this.disconnect(FatalErrors.WebsocketClosure);
      }
    }
    return wsUrl;
  }

  private connect(URL: URL) {
    this.URL = URL;
    this.webSocket = new ReconnectingWebSocket(this.getWsUrl.bind(this), undefined, { reconnectionDelayGrowFactor: 1 });
    this.webSocket.binaryType = "arraybuffer";

    this.receivedMessage = false;
    this.startReconnectTimer();

    this.websocketOpenHandler = (_event) => {
      console.log("websocket is open, sending join");
      //TODO: These events will need to shift with reconnections causing it more than once
      Logger.logEvent("monsterverse.launcher.websocketOpen");
      Logger.logPerfStats({ type: "NetworkingConnection", site: "launcher", status: "connected" });

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = undefined;
      }

      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer);
        this.timeoutTimer = undefined;
      }

      this.postMessage(LauncherEventType.NetworkConnected);

      this.sendPing();
      this.pingInterval = setInterval(this.sendPing.bind(this), pingTimerInSeconds * 1000);
    };

    this.websocketMessageHandler = (msg) => {
      if (!this.receivedMessage) {
        Logger.logEvent("monsterverse.launcher.websocketConnected");
        this.receivedMessage = true;
      }

      this.postMessage(LauncherEventType.NetworkMessage, msg.data);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const decoded = decode(msg.data) as any;
      const isLauncher = "launcher" in decoded;
      if (isLauncher) {
        this.onMessage(decoded);
      }
    };

    this.websocketCloseHandler = (close) => {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = undefined;
      }

      Logger.logInfo(`WebsocketClosure reason: ${close.reason} type: ${close.type} clean: ${close.wasClean}`);
      this.postMessage(LauncherEventType.NetworkLosing);
      this.startReconnectTimer();
    };

    this.webSocket.addEventListener("open", this.websocketOpenHandler);
    this.webSocket.addEventListener("message", this.websocketMessageHandler);
    //Using close instead of error because error events don't provide any data
    this.webSocket.addEventListener("close", this.websocketCloseHandler);
  }

  private disconnect(fireDisconnectEvent?: FatalErrors) {
    //Close websocket and remove event listeners
    if (this.webSocket) {
      this.webSocket.removeEventListener("open", this.websocketOpenHandler!);
      this.webSocket.removeEventListener("message", this.websocketMessageHandler!);
      this.webSocket.removeEventListener("close", this.websocketCloseHandler!);

      this.websocketOpenHandler = undefined;
      this.websocketMessageHandler = undefined;
      this.websocketCloseHandler = undefined;

      this.webSocket.close(WebSocketCloseCode.NormalClosure);
    }

    //Clear out the websocket
    this.webSocket = undefined;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }

    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = undefined;
    }

    if (fireDisconnectEvent) {
      this.postMessage(LauncherEventType.NetworkDisconnected);
    } else {
      this.postMessage(LauncherEventType.NetworkLosing);
    }

    //Clear received message indicator
    this.receivedMessage = false;

    if (fireDisconnectEvent) {
      ErrorHandling.instance.triggerFatalError(fireDisconnectEvent);
    }
  }

  private sendPing() {
    if (this.webSocket === undefined) {
      return;
    }

    //Send ping to keep connection alive
    this.webSocket.send(encode({ type: "Ping", launcher: true }));
    this.sentMessageCount++;
    this.pingSentTime = Date.now();
  }

  private startReconnectTimer() {
    if (this.reconnectTimeout !== undefined) {
      return;
    }

    this.reconnectTimeout = setTimeout(() => {
      Logger.logPerfStats({
        type: "NetworkingConnection",
        site: "launcher",
        status: this.receivedMessage ? "lost" : "disconnected",
      });
      console.log("Got reconnect timeout");
      this.disconnect(FatalErrors.ServerReconnectTimeout);
    }, failedReconnectTimeoutInSeconds * 1000);
  }

  private onMessage(message: { type: string; data?: string }) {
    switch (message.type) {
      case "Ping":
        this.receivedMessageCount++;
        Logger.logPerfStats({
          type: "Networking",
          site: "launcher",
          sentMessages: this.sentMessageCount,
          receivedMessages: this.receivedMessageCount,
          ping: Date.now() - this.pingSentTime,
          countryCode: this.countryCode,
          serverRegion: this.serverRegion,
          desiredServerRegion: this.desiredServerRegion,
        });
        this.sentMessageCount = 0;
        this.receivedMessageCount = 0;
        break;
      case "ServerShuttingDown":
        console.log("Got server shutting down");
        this.disconnect(FatalErrors.ServerShuttingDown);
        break;
      case "TicketRefresh":
        this.URL?.searchParams.set("ticket", message.data!);
        break;
    }

    this.receivedMessageCount++;
  }

  private postMessage(type: LauncherEventType, data: string | ArrayBuffer | undefined = undefined) {
    this.iframeEl.contentWindow?.postMessage({ iFrameLauncherEvent, type, data }, "*");
  }

  private onIFrameMessage(ev: MessageEvent<iFrameLauncherMessage>) {
    if (ev?.data?.iFrameLauncherEvent !== iFrameLauncherEvent) {
      return;
    }

    switch (ev.data.type) {
      case LauncherEventType.NetworkMessage:
        if (ev.data.data) {
          this.onNetworkMessage(ev.data.data as Uint8Array);
        }
        break;
      case LauncherEventType.ChangeIsland:
        {
          //Do a replace to not keep history (Android navigates "back" to close Monsterverse)
          const url = new URL(location.href);
          url.search = getUpdatedLauncherStartupOptions(ev.data.data as string);
          location.replace(url);
        }
        break;
      case LauncherEventType.Reload:
        location.reload();
        break;
    }
  }

  private onNetworkMessage(data: Uint8Array) {
    if (this.webSocket === undefined) {
      return;
    }

    this.webSocket.send(data);
    this.sentMessageCount++;
    this.resetInactivityTimer();
  }

  private resetInactivityTimer() {
    const env = window._ENV || "local";
    if (env !== "local" && this.loadingComplete) {
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer);
        this.timeoutTimer = undefined;
      }

      this.timeoutTimer = setTimeout(() => {
        console.log("Got inactivity timeout");
        this.disconnect(FatalErrors.ServerIdleTimeout);
      }, noActivityTimeoutInSeconds * 1000);
    }
  }
}
